import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import { mapLanguageLocale } from '@apps/main/modules/localization/localeUtils';
import {
  useEditingIngredientStore,
  useItineraryStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import type { MapComponent } from '@apps/main/new-desktop/modules/maps';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import type { MarkerData } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import type { Map, MapMovedEvent, MapTypeId, Marker } from '@libs/maps';
import { MapControl } from '@libs/maps';

import type { HighlightedHotel, HotelMapCardData } from '../../models';
import { useHotelSelectionStore } from '../hotelSelection/hotelSelectionStore';
import { sortDestinationProducts } from '../hotelSelection/hotelSelectionStoreLogic';

import {
  getClusterHotelMarkerFormatter,
  getClusterOptions,
  getHighlightedHotel,
  getHotelMapCardData,
  getHotelSelectionMarkers,
  getMarkerFormatters,
} from './hotelSelectionMapStoreLogic';

type MapProperties = InstanceType<typeof MapComponent>['$props'];

export const useHotelSelectionMapStore = defineStore(
  'HotelSelectionMapStore',
  () => {
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const hotelSelectionStore = useHotelSelectionStore();
    const editingIngredientStore = useEditingIngredientStore();
    const itineraryStore = useItineraryStore();
    const statsigEventsStore = useStatsigEventsStore();
    const routerStore = useRouterStore();
    const mapStore = useMapStore();

    // State
    const isMapCreated = ref<boolean>(false);
    const isFirstMapRendered = ref<boolean>(true);
    const highlightedHotel = ref<HighlightedHotel | undefined>(undefined);

    // Actions
    function setDynamicMapIntegration(integration: Map): void {
      isFirstMapRendered.value = false;
      isMapCreated.value = true;
      mapStore.setMapIntegration(integration);
    }

    function mapViewChanged(event: MapMovedEvent): void {
      mapStore.setMapMovedAndCreateNewPolygon(event);
    }

    function markerClicked(marker: Marker): void {
      statsigEventsStore.logHotelSelectionMapMarkerClicked();
      const data = marker.data as MarkerData;
      const ingredientIndex = recipeStore.getIngredientIndex(data.ingredientId);
      highlightedHotel.value = {
        ingredientId: data.ingredientId,
        ingredientIndex,
        hotelCode: data.hotelCode,
        showHotelMapCard: true,
      };
    }

    function hotelCardClicked(): void {
      if (!highlightedHotel.value || !hotelMapCardData.value) {
        throw new Error('Not possible!');
      }

      const hotelId = highlightedHotel.value;
      const index = recipeStore.getIngredientIndex(hotelId.ingredientId);
      routerStore.navigateToHotelDetailsInHotelSelection(
        index,
        hotelId.hotelCode
      );
    }

    function hideHotelCard(): void {
      highlightedHotel.value = undefined;
    }

    function toggleSearchByMovingMap(): void {
      mapStore.updateSearchByMapMovement(!mapStore.searchByMapMovement);
    }

    function hightlightOnMap(hotelCode: string | undefined): void {
      const ingredientId =
        editingIngredientStore.editingStayIngredient?.ingredientId;

      if (ingredientId) {
        const ingredientIndex = recipeStore.getIngredientIndex(ingredientId);
        highlightedHotel.value = getHighlightedHotel(
          hotelCode,
          ingredientId,
          ingredientIndex
        );
      }
    }

    function destroyMap(): void {
      mapStore.updateMapShouldCenterOnMarkers(true);
    }

    function setMapTypeId(newMapTypeId: MapTypeId): void {
      mapStore.setMapTypeId(newMapTypeId);
    }

    // Getters
    const mapProperties = computed<MapProperties>(() => ({
      markers: getHotelSelectionMarkers(
        mapStore.markers,
        highlightedHotel.value,
        topAvailableProductCodes.value
      ),
      markerFormatters: getMarkerFormatters(),
      clusterMarkerFormatters: getClusterHotelMarkerFormatter(),
      clusterOptions: getClusterOptions(),
      route: mapStore.mapRoute,
      showAllMarkers: mapStore.mapShouldCenterOnMarkers,
      startRectangle: mapStore.DEFAULT_RECTANGLE,
      gestureHandling: 'greedy',
      locale: mapLanguageLocale,
      mapControls: [MapControl.zoomControl, MapControl.mapTypeControl],
    }));

    const topAvailableProductCodes = computed<string[]>(() => {
      const TOP_NUMBER_HOTELS = 10;
      let selectedHotelCode: string | undefined;

      if (editingIngredientStore.editingStayIngredient?.ingredientId) {
        const hotelFromItinerary = itineraryStore.getHotelIngredientResponse(
          editingIngredientStore.editingStayIngredient.ingredientId
        );

        if (
          hotelFromItinerary &&
          hotelFromItinerary.suggested &&
          hotelFromItinerary.suggested.code
        ) {
          selectedHotelCode = hotelFromItinerary.suggested.code;
        }
      }

      const availableProducts = sortDestinationProducts(
        tripStore.destinationProducts,
        hotelSelectionStore.listSuggestionType,
        selectedHotelCode
      );

      return availableProducts
        .map((product) => product.hotelCode)
        .slice(0, TOP_NUMBER_HOTELS + (selectedHotelCode ? 1 : 0));
    });

    const isSettingMap = computed<boolean>(
      () => !tripStore.isLocationManuallyChanged && !mapStore.startingLocation
    );

    const hotelMapCardData = computed<HotelMapCardData | undefined>(() =>
      getHotelMapCardData(
        highlightedHotel.value,
        tripStore.getStaticInfo,
        getImageUrl
      )
    );

    return {
      // State
      isFirstMapRendered,
      isMapCreated,
      highlightedHotel,

      // Actions
      mapViewChanged,
      markerClicked,
      setDynamicMapIntegration,
      toggleSearchByMovingMap,
      hotelCardClicked,
      hideHotelCard,
      hightlightOnMap,
      destroyMap,
      setMapTypeId,

      // Getters
      mapProperties,
      isSettingMap,
      hotelMapCardData,
    };
  }
);
